/**
 * Web Push registration helper for the PAID DEWZ admin PWA.
 * Frontend equivalents are intentionally minimal — all heavy lifting
 * (VAPID, payload signing, delivery) happens on the backend.
 */
import { http } from "@/lib/api";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration("/")) ||
      (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("SW register failed", e);
    return null;
  }
}

export async function getSubscription() {
  if (!pushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enableAdminPush() {
  if (!pushSupported()) {
    throw new Error("Push not supported on this device. Try installing the app to your home screen first.");
  }
  if (Notification.permission === "denied") {
    throw new Error("Notifications are blocked. Enable them in browser settings, then try again.");
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied.");

  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Could not register service worker.");

  // Fetch the public VAPID key from the backend
  const { data } = await http.get("/public/vapid-public-key");
  const publicKey = data.public_key;
  if (!publicKey) throw new Error("Push not configured on the server.");

  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // Re-send to backend to ensure it's stored against this admin
    await http.post("/admin/push/subscribe", { subscription: existing.toJSON() });
    return existing.toJSON();
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await http.post("/admin/push/subscribe", { subscription: sub.toJSON() });
  return sub.toJSON();
}

export async function disableAdminPush() {
  const sub = await getSubscription();
  if (!sub) return;
  try {
    await http.post("/admin/push/unsubscribe", { subscription: sub.toJSON() });
  } catch (_e) {
    /* ignore */
  }
  try {
    await sub.unsubscribe();
  } catch (_e) {
    /* ignore */
  }
}

export async function sendAdminPushTest() {
  const { data } = await http.post("/admin/push/test");
  return data;
}

/** True when running as a standalone PWA (iOS or Android). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/** True on iOS Safari (Web Push requires iOS 16.4+ AND PWA install). */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}
